import { useAppSelector } from '@app/store/store';
import React, { useMemo } from 'react';

import { CaptionOverlayView } from '../../../../../components/caption-overlay-view';
import {
  DEFAULT_ACTIVE_WORD_COLOR,
  DEFAULT_CAPTIONS_POSITION,
} from '../../../../../const';
import type { CaptionPositionKey } from '../../../../../hooks/use-active-caption';
import {
  calculateActiveWordIndexInSegment,
  getActiveChunk,
} from '../../../../../hooks/use-active-caption';
import {
  selectActiveEditCaptions,
  selectRecordingTranscriptSegments,
} from '../../../../../selector';
import { CAPTION_FONT_OPTIONS } from '../../../../../utils';

type EditCaptionOverlayProps = {
  sourceTimeSec: number;
  controlsBarVisible?: boolean;
};

export const EditCaptionOverlay = ({
  sourceTimeSec,
  controlsBarVisible = true,
}: EditCaptionOverlayProps) => {
  const captions = useAppSelector(selectActiveEditCaptions);
  const segments = useAppSelector(selectRecordingTranscriptSegments);

  const fontOption = useMemo(() => {
    if (!captions.enabled) return CAPTION_FONT_OPTIONS[0];
    return (
      CAPTION_FONT_OPTIONS.find(f => f.id === captions.captions_style) ||
      CAPTION_FONT_OPTIONS[0]
    );
  }, [captions]);

  const captionsPosition: CaptionPositionKey = captions.enabled
    ? captions.captions_position
    : DEFAULT_CAPTIONS_POSITION;

  const highlightColor = captions.enabled
    ? captions.highlight_color
    : DEFAULT_ACTIVE_WORD_COLOR;

  const { text, words, activeWordIndex } = useMemo(() => {
    if (!captions.enabled || !segments?.length) {
      return {
        text: null as string | null,
        words: [] as string[],
        activeWordIndex: -1,
      };
    }

    const currentTimeMs = sourceTimeSec * 1000;
    const activeSegment =
      segments.find(
        seg =>
          currentTimeMs >= seg.start * 1000 && currentTimeMs <= seg.end * 1000,
      ) ?? null;

    if (!activeSegment?.text) {
      return { text: null, words: [], activeWordIndex: -1 };
    }

    const allWords = activeSegment.text.split(/\s+/).filter(w => w.length > 0);
    const activeWordInSegment = calculateActiveWordIndexInSegment(
      activeSegment.start,
      activeSegment.end,
      sourceTimeSec,
      allWords,
    );
    const { chunkWords, activeIndexInChunk } = getActiveChunk(
      allWords,
      activeWordInSegment,
    );

    return {
      text: activeSegment.text,
      words: chunkWords,
      activeWordIndex: activeIndexInChunk,
    };
  }, [captions.enabled, segments, sourceTimeSec]);

  return (
    <CaptionOverlayView
      visible={captions.enabled}
      text={text}
      words={words}
      activeWordIndex={activeWordIndex}
      highlightColor={highlightColor}
      fontFamily={fontOption.fontFamily}
      fontWeight={fontOption.fontWeight}
      fontScale={fontOption.fontScale}
      captionsPosition={captionsPosition}
      controlsBarVisible={controlsBarVisible}
    />
  );
};
